import { Color, Spacing } from '@persona-id/design-tokens';
import { LinkExternalIcon, StarFillIcon } from '@persona-id/design-tokens/icons';
import styled from 'styled-components';

import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import usePlanRequirements from 'dashboard/components/PlanUpgradeFlow/usePlanRequirements';
import { ProductFeatureName } from 'dashboard/models/organization';
import {
  BuilderName,
  VERIFICATION_BUILDER_NAME_TO_PREVIEW,
} from 'dashboard/models/verification-template';
import { createPreviewDocumentGeneric } from 'dashboard/modules/CaseModuleBase/previewBuilders/document';
import ResourceVerification from 'dashboard/modules/ResourceVerification';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';

const TemplatePageContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
`;

const LeftColumn = styled.div`
  width: 300px;
  flex: none;
  height: 100%;
  padding: ${Spacing[5]};
  border-right: 1px solid ${({ theme }) => theme.color.border.secondary};
`;

const TemplateDescription = styled(Paragraph)`
  line-height: 22px;
`;

const RightColumnWrapper = styled.div`
  overflow-y: visible;
  overflow-x: scroll;
  display: flex;
  flex-direction: column;
  position: relative;
  flex: auto;
`;

const RightColumn = styled.div`
  display: flex;
  flex: auto;
  overflow-x: auto;
  width: 100%;
  background-color: ${Color.CoreSlate50};
  align-items: end;
  justify-content: center;
`;

const PreviewContainer = styled.div`
  overflow-y: visible;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: ${Spacing[4]};
  flex: auto;
`;

const PageFooter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  height: ${Spacing[9]};
  padding: ${Spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
  width: 100%;
`;

const ConfirmationButton = styled(Button).attrs(() => ({ variant: 'primary' }))`
  width: 160px;
`;

const LearnMoreLink = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${Spacing[1]};
  margin-top: ${Spacing[4]};
`;

interface CreateVerificationTemplatePageProps {
  builderId: BuilderName;
  description: string;
  helpCenterLink: string;
  requiredProductFeatures: ProductFeatureName[];
  requiresOrderForm: boolean;
  handleCreateVerificationTemplate: (builderId: BuilderName) => Promise<void>;
  handlePlanUpgradeFlow: () => void;
  handleAddOnFlow: () => void;
}

const CreateVerificationTemplatePage = ({
  builderId,
  description,
  helpCenterLink,
  requiredProductFeatures,
  requiresOrderForm: builderRequiresOrderForm,
  handleCreateVerificationTemplate,
  handlePlanUpgradeFlow,
  handleAddOnFlow,
}: CreateVerificationTemplatePageProps) => {
  const { requiresPlanUpgrade, requiresAddOn } = usePlanRequirements(
    requiredProductFeatures,
    builderRequiresOrderForm,
  );

  return (
    <TemplatePageContainer>
      <LeftColumn>
        {requiresAddOn ? (
          <>
            <Sticker icon={<StarFillIcon />} variant='purple'>
              Add-on
            </Sticker>
            <Spacer height={2} />
          </>
        ) : requiresPlanUpgrade ? (
          <div>
            <InquiriesGatingSticker
              dataMarketingIdSuffix={`${builderId}-limited-sticker`}
              stickerType={EnhancedGatingStickerType.Limited}
              iconOnly={false}
            />
            <br />
          </div>
        ) : null}
        <TemplateDescription>{description}</TemplateDescription>
        {helpCenterLink && (
          <a href={helpCenterLink} rel='noreferrer' target='_blank'>
            <LearnMoreLink>
              Learn more
              <LinkExternalIcon />
            </LearnMoreLink>
          </a>
        )}
      </LeftColumn>
      <RightColumnWrapper>
        <RightColumn>
          <PreviewContainer>
            <ResourceVerification
              verification={VERIFICATION_BUILDER_NAME_TO_PREVIEW[builderId]}
              documents={[createPreviewDocumentGeneric()]}
              defaultAccordionOpen={true}
              featureFlags={{} as FeatureFlagsMap}
            />
          </PreviewContainer>
        </RightColumn>
        <PageFooter>
          {requiresAddOn ? (
            <ConfirmationButton
              id='create-template__confirmation-button'
              onClick={() => handleAddOnFlow()}
            >
              Request template
            </ConfirmationButton>
          ) : requiresPlanUpgrade ? (
            <ConfirmationButton
              id='create-template__confirmation-button'
              onClick={() => handlePlanUpgradeFlow()}
            >
              Request template
            </ConfirmationButton>
          ) : (
            <ConfirmationButton
              id='create-template__confirmation-button'
              onClick={() => handleCreateVerificationTemplate(builderId)}
            >
              Use template
            </ConfirmationButton>
          )}
        </PageFooter>
      </RightColumnWrapper>
    </TemplatePageContainer>
  );
};

export default CreateVerificationTemplatePage;
